import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { ElementRef, Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { DynamoData, DynamoDataV2, Subtitle, Video, VideoPageComponent } from '@interfaces';
import Hls from 'hls.js';
import { VideosService } from 'src/app/rest/videos.service';
import { GtagService, VideoGtagEvent } from '../containers/gtag/gtag.service';
import { ComponentActionsService } from '../services/component-actions.service';
import { AbstractComponent } from './abstract-component';

@Injectable()
export abstract class AbstractVideoComponent extends AbstractComponent implements OnDestroy {
    public videoElement: ElementRef;
    public error = false;
    public loading = true;
    public sourceLoaded = false;
    public playing = false;
    public paused = false;
    public ended = false;
    public displayFullScreen = false;
    public showNativeControls = true;
    public subtitles: Subtitle[] = [];
    public subtitlesShow = 'disabled';
    public playlist: Video[] = [];
    public videoData: Video;
    public originVideo: Video;
    public playbackDetails = {
        duration: '0:00:00',
        progress: '0:00:00',
    };
    private hls: Hls;
    private playProgress = 0;


    constructor(
        protected videosService: VideosService,
        protected actionsService: ComponentActionsService,
        @Inject(PLATFORM_ID) protected platform,
        @Inject(DOCUMENT) protected document,
        protected breakpointObserver: BreakpointObserver,
        protected gtag: GtagService,
    ) {
        super(actionsService);

        const layoutChanges = breakpointObserver.observe([
            Breakpoints.HandsetPortrait,
            Breakpoints.HandsetLandscape
        ]);

        layoutChanges.subscribe(result => {
            this.displayFullScreen = result.matches;
        });
    }

    ngOnDestroy() {
        this.unloadSource();
        this.videoElement?.nativeElement.removeEventListener('play', this.handlePlay.bind(this));
        this.videoElement?.nativeElement.removeEventListener('stop', this.handleStop.bind(this), true);
        this.videoElement?.nativeElement.removeEventListener('pause', this.handlePause.bind(this), true);
        this.videoElement?.nativeElement.removeEventListener('ended', this.handleEnded.bind(this), true);
        this.videoElement?.nativeElement.removeEventListener('timeupdate', this.handleTimeUpdate.bind(this), true);
    }

    public loadSource(videoData: Video, reload = false) {
        const Hls = this.document.defaultView['Hls'];
        if(!Hls) return;
        if (reload || !this.sourceLoaded) {
            this.sourceLoaded = true;
            if (Hls.isSupported()) {
                this.hls = new Hls({ autoStartLoad: false, capLevelToPlayerSize: false });
                this.hls.loadSource(videoData.dynamoData.hlsUrl);
                this.hls.attachMedia(this.videoElement.nativeElement);
                this.hls.startLevel = -1;
                // Otherwise default to loading as a plan video source
            } else if ((this.videoElement.nativeElement as HTMLVideoElement).canPlayType('application/vnd.apple.mpegurl')) {
                if (videoData.pipelineVersion === 2) {
                    this.attachMediaToElement((videoData.dynamoData as any));
                } else {
                    this.attachLegacyMediaToElement((videoData.dynamoData as any));
                }
            }
        }
    }

    public unloadSource() {
        this.sourceLoaded = false;
        this.playing = false;
        if (this.hls) {
            this.hls.destroy();
        }
    }

    public setPoster() {
        // Layout
        let width = 'w_auto';
        let thumbnail = this.videoData.thumbnail;

        if (!this.mobileMode && this.videoData?.customThumbnail?.public_id) {
            thumbnail = `https://res.cloudinary.com/minirobot/image/upload/f_auto,q_auto,c_fill,${width}/v${this.videoData.customThumbnail.version}/${this.videoData.customThumbnail.public_id}`;
        } else if (this.mobileMode && this.videoData?.customMobileThumbnail?.public_id) {
            thumbnail = `https://res.cloudinary.com/minirobot/image/upload/f_auto,q_auto,c_fill,${width}/v${this.videoData.customMobileThumbnail.version}/${this.videoData.customMobileThumbnail.public_id}`;
        } else if (this.mobileMode && this.videoData?.customThumbnail?.public_id) {
            thumbnail = `https://res.cloudinary.com/minirobot/image/upload/f_auto,q_auto,c_fill,${width}/v${this.videoData.customThumbnail.version}/${this.videoData.customThumbnail.public_id}`;
        }
        this.videoElement?.nativeElement?.setAttribute('poster', thumbnail);
    }

    public loadVideo(videoData: Video) {
        this.videoData = videoData;
        if (!videoData?.dynamoData || videoData?.dynamoData?.workflowStatus?.toLowerCase() !== 'complete' || isPlatformServer(this.platform)) {
            return;
        } else {

            this.videoElement.nativeElement.setAttribute('height', '100%');
            this.videoElement.nativeElement.setAttribute('width', '100%');
            this.videoElement.nativeElement.innerText = 'This browser does not support the HTML5 video element.';
            this.videoElement.nativeElement.setAttribute('playsinline', 'playsinline');

            // Set poster
            this.setPoster();

            // Behaviour
            this.videoElement.nativeElement.setAttribute('crossorigin', 'crossorigin');
            this.videoElement.nativeElement.setAttribute('preload', 'metadata');


            // Autoplay config
            if ((this.component as VideoPageComponent)?.autoplay) {
                this.loadSource(this.videoData);
                (this.videoElement.nativeElement as HTMLVideoElement).autoplay = true;
                (this.videoElement.nativeElement as HTMLVideoElement).muted = true;
                (this.videoElement.nativeElement as HTMLVideoElement).play();
            }

            // Subtitle menu wireup
            this.videoData.subtitles?.forEach((subtitle) => {
                const track = document.createElement('track');
                track.setAttribute('kind', 'subtitles');
                track.setAttribute('srclang', subtitle.lang);
                track.setAttribute('label', subtitle.label);
                track.setAttribute('src', subtitle.src);
                (this.videoElement.nativeElement as HTMLVideoElement).appendChild(track);
            });

            // GA Event Wireups
            this.videoElement.nativeElement.removeEventListener('play', this.handlePlay.bind(this));
            this.videoElement.nativeElement.addEventListener('play', this.handlePlay.bind(this), true);

            this.videoElement.nativeElement.removeEventListener('stop', this.handleStop.bind(this), true);
            this.videoElement.nativeElement.addEventListener('stop', this.handleStop.bind(this), true);

            this.videoElement.nativeElement.removeEventListener('pause', this.handlePause.bind(this), true);
            this.videoElement.nativeElement.addEventListener('pause', this.handlePause.bind(this), true);

            this.videoElement.nativeElement.removeEventListener('ended', this.handleEnded.bind(this), true);
            this.videoElement.nativeElement.addEventListener('ended', this.handleEnded.bind(this), true);

            this.videoElement.nativeElement.removeEventListener('timeupdate', this.handleTimeUpdate.bind(this), true);
            this.videoElement.nativeElement.addEventListener('timeupdate', this.handleTimeUpdate.bind(this), true);
        }
    }

    private handlePlay() {
        const Hls = this.document.defaultView['Hls'];
        if(!Hls) return;
        this.loadSource(this.videoData);
        if (!this.noTracking && isPlatformBrowser(this.platform)) {
            this.gtag.fireVideoEvent(VideoGtagEvent.PLAY, this.videoData._id, this.videoData.name, this.playProgress);
        }

        if (Hls.isSupported()) {
            this.hls.startLoad(-1);
        }
        if (this.showNativeControls && (!(this.component as VideoPageComponent)?.autoplay)) {
            (this.videoElement.nativeElement as HTMLVideoElement).controls = true;
        }
        (this.videoElement.nativeElement as HTMLVideoElement).removeAttribute('poster');
        this.playing = true;
        this.paused = false;
        this.ended = false;
    }

    private handleStop() {
        if (!this.noTracking && isPlatformBrowser(this.platform)) {
            this.gtag.fireVideoEvent(VideoGtagEvent.STOP, this.videoData._id, this.videoData.name, this.playProgress);
        }
        if (!(this.component as VideoPageComponent)?.autoplay && this.showNativeControls) {
            (this.videoElement.nativeElement as HTMLVideoElement).controls = true;
        }

        this.setPoster();
        this.playing = false;
        this.paused = false;
        this.playProgress = 0;
    }

    private handlePause() {
        this.playing = false;
        this.paused = true;
        if (this.showNativeControls) {
            (this.videoElement.nativeElement as HTMLVideoElement).controls = true;
        }
    }

    private handleEnded() {
        if ((this.component as VideoPageComponent)?.autoplay) return;
        if (!this.noTracking && isPlatformBrowser(this.platform)) {
            this.gtag.fireVideoEvent(VideoGtagEvent.ENDED, this.videoData._id, this.videoData.name, this.playProgress);
        }
        (this.videoElement.nativeElement as HTMLVideoElement).load();
        (this.videoElement.nativeElement as HTMLVideoElement).controls = false;
        this.setPoster();
        this.playing = false;
        this.paused = false;
        this.ended = true;
        this.playProgress = 0;
    }

    public handleTimeUpdate(event: Event) {
        const duration = this.videoElement.nativeElement?.duration || 0;
        const progress = event?.target['currentTime'] || 0;
        const progressPc = (100 * progress) / duration;

        // Every 10% played, fire a GTM event
        if (!this.noTracking && isPlatformBrowser(this.platform)) {
            if (progressPc > (this.playProgress + 10)) {
                this.playProgress += 10;
                this.gtag.fireVideoEvent(VideoGtagEvent.PROGRESS, this.videoData._id, this.videoData.name, this.playProgress);
            }
        }

        let durationHours = Math.floor(duration / 3600).toString().padStart(2, '0'),
            durationMinutes = Math.floor(duration % 3600 / 60).toString().padStart(2, '0'),
            durationSeconds = Math.floor(duration % 3600 % 60).toString().padStart(2, '0');

        let progressHours = Math.floor(progress / 3600).toString().padStart(2, '0'),
            progressMinutes = Math.floor(progress % 3600 / 60).toString().padStart(2, '0'),
            progressSeconds = Math.floor(progress % 3600 % 60).toString().padStart(2, '0');


        this.playbackDetails = {
            duration: `${durationHours}:${durationMinutes}:${durationSeconds}`,
            progress: `${progressHours}:${progressMinutes}:${progressSeconds}`,
        };
    }

    /**
     * Toggle playback and playback state.
     */
    public async togglePlay() {
        if (this.playing) {
            return await (this.videoElement.nativeElement as HTMLVideoElement).pause();
        } else {

            // Pause all other tags
            const videoTags = document.getElementsByTagName('video');
            Array.from(videoTags).forEach((el: HTMLVideoElement) => !el.autoplay ? el.pause() : null);
            if (this.displayFullScreen && (this.videoElement.nativeElement as HTMLVideoElement).requestFullscreen) {
                await (this.videoElement.nativeElement as HTMLVideoElement).requestFullscreen();
            }
            return await (this.videoElement.nativeElement as HTMLVideoElement).play();
        }
    }

    public async makeFullscreen() {
        await (this.videoElement.nativeElement as HTMLVideoElement).requestFullscreen();
    }

    /**
     * Provide iOS with options for playback.
     * @param videoData
     */
    private attachLegacyMediaToElement(videoData: DynamoData) {
        const sources = [{
            src: videoData.hlsUrl.replace('https://d2ee1no1x9ct7i.cloudfront.net', 'https://video.voyse.io'),
            type: 'application/x-mpegurl'
        }, {
            src: `https://video.voyse.io/${videoData.mp4Outputs[0]}`,
            type: 'video/mp4'
        }];

        const srcEls = sources.map(source => {
            let el = document.createElement('source');
            el.setAttribute('src', source.src);
            el.setAttribute('type', source.type);
            return el;
        });

        srcEls.forEach(source => {
            this.videoElement.nativeElement.appendChild(source);
        });
    }

    /**
    * Provide iOS with options for playback.
    * @param videoData
    */
    private attachMediaToElement(videoData: DynamoDataV2) {
        const sources = [{
            src: videoData.hlsUrl.replace('https://d1asah1rj7d0np.cloudfront.net', 'https://videov2.voyse.io'),
            type: 'application/x-mpegurl'
        }];

        const srcEls = sources.map(source => {
            let el = document.createElement('source');
            el.setAttribute('src', source.src);
            el.setAttribute('type', source.type);
            return el;
        });

        srcEls.forEach(source => {
            this.videoElement.nativeElement.appendChild(source);
        });
    }

    public toggleSubtitles() {
        if (this.videoElement.nativeElement.textTracks.length) {
            const track = this.videoElement.nativeElement.textTracks[0];
            if (this.subtitlesShow === 'disabled') {
                this.subtitlesShow = 'showing'
            } else {
                this.subtitlesShow = 'disabled';
            }
            track.mode = this.subtitlesShow;
        }
    }

    public async pause() {
        return await (this.videoElement.nativeElement as HTMLVideoElement).pause();
    }

    public async play() {
        return await (this.videoElement.nativeElement as HTMLVideoElement).play();
    }

}
